import type { TApplicantStatus } from '@/services/applicants';
import type { TMemberEmailPurpose } from '@/services/emailSends';
import type { TPermission } from '@/constants/permissions';

type TStatusActionVariant = 'primary' | 'secondary' | 'neutral';

type TStatusActionSpec = {
  fromStatus: TApplicantStatus;
  toStatus: TApplicantStatus;
  label: string;
  variant: TStatusActionVariant;
  permission: TPermission;
  isGatedByChecks?: boolean;
  isGatedBySeat?: boolean;
  isGatedByScore?: boolean;
  isGatedByAssessment?: boolean;
  emailPurpose?: TMemberEmailPurpose;
};

const REJECT_STATUS: TApplicantStatus = 'rejected';

const REJECT_EMAIL_PURPOSE: TMemberEmailPurpose = 'waitlist_update';

const APPROVE_STATUS: TApplicantStatus = 'approved_waitlisted';

const OPEN_SEAT_STATUS: TApplicantStatus = 'activation_ready';

const FINAL_STATUSES: readonly TApplicantStatus[] = ['rejected', 'barred'];

const STATUS_ACTIONS: readonly TStatusActionSpec[] = [
  {
    fromStatus: 'applied',
    toStatus: 'recruiter_review',
    label: 'Advance to review',
    variant: 'primary',
    permission: 'applicants:advance',
  },
  {
    fromStatus: 'recruiter_review',
    toStatus: 'interview_invited',
    label: 'Invite to interview',
    variant: 'primary',
    permission: 'applicants:advance',
    emailPurpose: 'step_interview',
  },
  {
    fromStatus: 'interview_scheduled',
    toStatus: 'verification_pending',
    label: 'Send to checks',
    variant: 'primary',
    permission: 'applicants:advance',
    isGatedByScore: true,
    isGatedByAssessment: true,
    emailPurpose: 'step_verification',
  },
  {
    fromStatus: 'verification_pending',
    toStatus: 'pending_final_approval',
    label: 'Send to final approval',
    variant: 'primary',
    permission: 'applicants:advance',
    isGatedByChecks: true,
  },
  {
    fromStatus: 'pending_final_approval',
    toStatus: 'approved_waitlisted',
    label: 'Approve',
    variant: 'primary',
    permission: 'final-approval:decide',
    emailPurpose: 'approved',
  },
  {
    fromStatus: 'approved_waitlisted',
    toStatus: 'activation_ready',
    label: 'Open a seat',
    variant: 'primary',
    permission: 'applicants:open-seat',
    isGatedBySeat: true,
    emailPurpose: 'activation_link',
  },
  {
    fromStatus: 'activation_ready',
    toStatus: 'approved_waitlisted',
    label: 'Release seat',
    variant: 'secondary',
    permission: 'applicants:open-seat',
  },
];

const STATUS_ACTION_COPY = {
  none: 'Moves on by the applicant or a webhook',
  locked: 'Frozen by a terminal disqualification',
  blockedByChecks: 'Waiting on both verification checks',
  blockedByScore: 'Score the interview first',
  blockedByAssessment: 'Assess the interview first',
  final: 'Rejected — this decision is final',
} as const;

const STATUS_EMAIL_COPY = { notSent: 'The status changed, but the email to the applicant was not sent' } as const;

const STATUS_ERROR_COPY: Record<string, string> = {
  '23514': 'The database refused this move. Refresh the record and check the verification result.',
  '22023': 'A rejected photo needs a note. Write the photo note on the record, then reject.',
  'interview_upcoming': 'The interview call is still ahead. You can reject once the call time has come.',
  'not_permitted': 'Only a Super-Admin opens a seat.',
  'no_email': 'Add an email address first — the app link goes by email.',
  'payment_required': 'A Client\'s seat opens after payment.',
  'no_free_seat': 'All seats in this market are taken.',
  'not_configured': 'The app download link is not set yet.',
};

export type {
  TStatusActionSpec,
  TStatusActionVariant,
};
export {
  APPROVE_STATUS,
  FINAL_STATUSES,
  OPEN_SEAT_STATUS,
  REJECT_EMAIL_PURPOSE,
  REJECT_STATUS,
  STATUS_ACTIONS,
  STATUS_ACTION_COPY,
  STATUS_EMAIL_COPY,
  STATUS_ERROR_COPY,
};
